import { BaseApiService } from '@/services/base-service';
import {
  ApiPagination, BaseResponse, Filters, FiltersPagination, RequestPayload, Response,
} from '@/types';
import {
  SoftwareLicenses,
  SoftwareLicensesStatuses,
  SoftwareLicenseCategories,
  SoftwareLicenseHistory,
  SoftwareLicensesFilters,
  SoftwareCartHistory,
} from '@/modules/software-licenses/types';
import { Clients } from '@/modules/clients/types';

interface SoftwareLicensesList extends BaseResponse, ApiPagination {
  data: {
    items: SoftwareLicenses.SoftwareLicense[],
    cities: string[],
    statuses: SoftwareLicensesStatuses[],
    categories: SoftwareLicenseCategories[],
    isSoftwareOrderAllowed: boolean,
  }
}

interface SoftwareLicensesHistoryList extends BaseResponse, ApiPagination {
  data: {
    items: SoftwareLicenseHistory[],
    statuses: SoftwareLicensesStatuses[],
  }
}

interface SoftwareCartHistoryList extends BaseResponse, ApiPagination {
  data: {
    items: SoftwareCartHistory[],
  }
}

export class SoftwareLicensesService extends BaseApiService {
  private readonly base_path = 'partner/software_licenses';

  async get_software_licenses(filters: FiltersPagination<Filters>): Promise<SoftwareLicensesList> {
    const payload = filters;

    const { data } = await this.http_client.get<SoftwareLicensesList>(this.base_path, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async get_not_active_software_licenses(payload: RequestPayload<SoftwareLicenses.NotActiveSoftwareLicensesRequestFilters>): Promise<SoftwareLicenses.NotActiveSoftwareLicensesResponse> {
    const payload_filters = payload.filters;

    payload_filters.search = payload_filters.search ? payload_filters.search : '';

    const { data } = await this.http_client.get<SoftwareLicenses.NotActiveSoftwareLicensesResponse>(`${this.base_path}/not_active`, {
      params: {
        filters: payload_filters,
        filters_exist: payload.filters_exist,
        page: payload.pagination.page,
        limit: payload.pagination.limit,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async get_software_licenses_history(filters: FiltersPagination<Filters>): Promise<SoftwareLicensesHistoryList> {
    const payload = filters;

    const { data } = await this.http_client.get<SoftwareLicensesHistoryList>(`${this.base_path}/history`, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async get_software_license_by_id(id: string | number): Promise<Response<SoftwareLicenses.SoftwareLicense>> {
    const url = `${this.base_path}/${id}`;
    const { data } = await this.http_client.get<Response<SoftwareLicenses.SoftwareLicense>>(url);

    return data;
  }

  async get_software_cart_history(filters: FiltersPagination<Filters>, id: string | number): Promise<SoftwareCartHistoryList> {
    const url = `${this.base_path}/${id}/cart_history`;
    const payload = filters;

    const { data } = await this.http_client.get<SoftwareCartHistoryList>(url, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async licenses_in_archive(payload: (string | number | undefined)[]) {
    await this.http_client.post(`${this.base_path}/licenses-in-archive`, { ids: payload });
  }
}

export const default_software_license = (): SoftwareLicenses.SoftwareLicense => ({
  organization_name: '',
  client_name: '',
  client_type: '',
  client_contact: '',
  client_email: '',
  client_phone: '',
  client_inn: '',
  client_kpp: '',
  client_country: '',
  client_region: '',
  client_city: '',
  nomenclature: '',
  license_key: '',
  client_date_activation: '',
  client_date_expired: '',
  status: {
    name: '',
    slug: '',
  },
  doc: '',
  client_shop: '',
  client_device: '',
  has_detail: false,
  is_available_add_cart: false,
  serial_prolonged: '',
  date_prolonged: '',
});

export const default_not_active_software_licenses_filters = (): SoftwareLicenses.NotActiveSoftwareLicensesRequestFilters => ({
  search: '',
  statuses: null,
});

export const default_software_licenses_history_filters = (): SoftwareLicensesFilters => ({
  search: '',
  statuses: [],
  dates: [],
});
